'use client';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
  JSX,
} from 'react';
import useAuth from '../hooks/useAuth';

export type AxiosPostType = <T = any, B = any>(
  url: string,
  data?: B,
  config?: AxiosRequestConfig<B>,
) => Promise<AxiosResponse<T>>;

export type AxiosGetType = <T = any>(
  url: string,
  config?: AxiosRequestConfig,
) => Promise<AxiosResponse<T>>;

export type AxiosDeleteType = <T = any>(
  url: string,
  config?: AxiosRequestConfig,
) => Promise<AxiosResponse<T>>;

export type FetchContextProps = {
  post: AxiosPostType;
  get: AxiosGetType;
  put: AxiosPostType;
  patch?: AxiosPostType;
  axiosDelete: AxiosDeleteType;
};

const axiosInstance = axios.create({ headers: { 'as-xhr': true } });

const FetchContext = React.createContext({
  post: axiosInstance.post,
  get: axiosInstance.get,
  put: axiosInstance.put,
  patch: axiosInstance.patch,
  axiosDelete: axiosInstance.delete,
});

/**
 * Provider to wrap around the app and provide appropriate headers.
 */
export const FetchProvider = ({
  children,
  csrfToken: _csrf_token,
}: PropsWithChildren<{ csrfToken?: string }>): JSX.Element => {
  const { signIn } = useAuth();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.request.use((config) => {
      if (_csrf_token) config.data = { ...config.data, _csrf_token };

      return config;
    });

    return () => axiosInstance.interceptors.request.eject(interceptor);
  }, [_csrf_token]);

  useEffect(() => {
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (!redirecting && [401, 440].includes(error?.response?.status)) {
          setRedirecting(true);
          signIn(window.location.pathname + window.location.search);
        }

        return Promise.reject(error);
      },
    );

    return () => axiosInstance.interceptors.response.eject(interceptor);
  }, [redirecting]);

  const contextValue = useMemo(
    () => ({
      post: axiosInstance.post,
      get: axiosInstance.get,
      put: axiosInstance.put,
      patch: axiosInstance.patch,
      axiosDelete: axiosInstance.delete,
    }),
    [],
  );

  return (
    <FetchContext.Provider value={contextValue}>
      {children}
    </FetchContext.Provider>
  );
};

/**
 * Hook to use the fetch context.
 */
export const useFetch = (): FetchContextProps => useContext(FetchContext);
